// src/pages/store/Storelogin.tsx - TYPESCRIPT FIXED VERSION
import React, { useState, useEffect } from 'react';

// ============================================
// TYPE DEFINITIONS
// ============================================
interface Store {
  storeId: string;
  storeName: string;
  businessId: string;
  manager: string;
  email: string;
  latitude: number;
  longitude: number;
  isActive: boolean;
}

interface GPSLocation {
  latitude: number;
  longitude: number;
}

interface ActivationResult {
  success: boolean;
  message: string;
  lotteryNumber?: string;
  lotteryName?: string;
}

interface Stats {
  totalEntries: number;
  pendingEntries: number;
  winnersCount: number;
  todayEntries: number;
  activeLotteries: number;
  winRate: string;
}

// ============================================
// 1. STORE LOGIN COMPONENT
// ============================================
const StoreLogin: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate }) => {
  const [storeId, setStoreId] = useState('STORE-1760156880519406');
  const [password, setPassword] = useState('MyPassword123!');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/store-lottery/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: storeId.trim(), password })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Login failed');
      }

      localStorage.setItem('storeToken', data.data.token);
      localStorage.setItem('storeData', JSON.stringify(data.data.store));
      console.log('✅ Login successful:', data.data.store.storeName);
      
      if (onNavigate) onNavigate('dashboard');
      else window.location.href = '/store/dashboard';
    } catch (err) {
      const error = err as Error;
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🏪</div>
          <h1 className="text-3xl font-bold text-gray-900">Store Portal</h1>
          <p className="text-gray-600 mt-2">Activate tags for lottery</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              ⚠️ {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Store ID</label>
              <input
                type="text"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="STORE-XXXXXXXXXXXXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter password"
              />
            </div>

            <button
              onClick={handleLogin}
              disabled={loading}
              className={`w-full py-3 rounded-lg font-semibold text-white ${
                loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? '⏳ Signing in...' : '🔐 Sign In'}
            </button>
          </div>
        </div>

        <div className="mt-4 bg-gray-800 text-white rounded-lg p-4 text-xs">
          <p className="font-semibold mb-2">🧪 Demo Credentials:</p>
          <p>Store: STORE-1760156880519406</p>
          <p>Pass: MyPassword123!</p>
        </div>
      </div>
    </div>
  );
};

// ============================================
// 2. STORE DASHBOARD
// ============================================
const StoreDashboard: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate }) => {
  const [store, setStore] = useState<Store | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storeData = localStorage.getItem('storeData');
    if (!storeData) {
      if (onNavigate) onNavigate('login');
      else window.location.href = '/store/login';
      return;
    }

    const parsedStore = JSON.parse(storeData) as Store;
    setStore(parsedStore);
    loadStats();
  }, [onNavigate]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('storeToken');
      const response = await fetch('/store-lottery/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      }
    } catch (err) {
      console.error('Stats load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    if (onNavigate) onNavigate('login');
    else window.location.href = '/store/login';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                🏪 {store?.storeName}
              </h1>
              <p className="text-sm text-gray-500">Store ID: {store?.storeId}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-3xl font-bold text-blue-600">{stats?.totalEntries || 0}</div>
            <div className="text-sm text-gray-600 mt-1">Total Entries</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-3xl font-bold text-yellow-600">{stats?.pendingEntries || 0}</div>
            <div className="text-sm text-gray-600 mt-1">Pending</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-3xl font-bold text-green-600">{stats?.winnersCount || 0}</div>
            <div className="text-sm text-gray-600 mt-1">Winners</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-3xl font-bold text-purple-600">{stats?.todayEntries || 0}</div>
            <div className="text-sm text-gray-600 mt-1">Today</div>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            onClick={() => onNavigate ? onNavigate('activate') : window.location.href = '/store/activate'}
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-left"
          >
            <div className="text-5xl mb-4">🎟️</div>
            <h3 className="text-2xl font-bold mb-2">Activate Tag</h3>
            <p className="text-blue-100">Scan tag to enter lottery</p>
          </button>

          <button
            onClick={() => onNavigate ? onNavigate('history') : window.location.href = '/store/history'}
            className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-left"
          >
            <div className="text-5xl mb-4">📊</div>
            <h3 className="text-2xl font-bold mb-2">View History</h3>
            <p className="text-purple-100">See all activations</p>
          </button>
        </div>

        {/* Active Lottery Info */}
        <div className="mt-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-6">
          <div className="flex items-start space-x-4">
            <div className="text-4xl">🎯</div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Active Lottery: NTMAXLSI Holiday Giveaway 2024
              </h3>
              <div className="space-y-1 text-sm text-gray-700">
                <p>🏆 Grand Prize: iPhone 15 Pro</p>
                <p>🎧 2nd Prize: AirPods Pro (3 winners)</p>
                <p>🎁 3rd Prize: $50 Gift Card (10 winners)</p>
                <p className="mt-2 font-semibold">Draw Date: Dec 31, 2024</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// 3. TAG ACTIVATION PAGE
// ============================================
const TagActivation: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate }) => {
  const [store, setStore] = useState<Store | null>(null);
  const [tagId, setTagId] = useState('');
  const [gpsLocation, setGpsLocation] = useState<GPSLocation | null>(null);
  const [gpsChecking, setGpsChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ActivationResult | null>(null);

  useEffect(() => {
    const storeData = localStorage.getItem('storeData');
    if (!storeData) {
      if (onNavigate) onNavigate('login');
      return;
    }
    setStore(JSON.parse(storeData) as Store);
    getGPSLocation();
  }, [onNavigate]);

  const getGPSLocation = () => {
    setGpsChecking(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setGpsChecking(false);
        },
        (error) => {
          alert('⚠️ GPS required. Please enable location.');
          setGpsChecking(false);
        },
        { enableHighAccuracy: true }
      );
    } else {
      alert('⚠️ GPS not supported');
      setGpsChecking(false);
    }
  };

  const handleActivate = async () => {
    if (!tagId.trim()) {
      alert('Please enter Tag ID');
      return;
    }

    if (!gpsLocation) {
      alert('⚠️ GPS location required');
      getGPSLocation();
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem('storeToken');
      const response = await fetch('/store-lottery/activate-tag', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tagId: tagId.toUpperCase(),
          batchId: 'BATCH-20250918-67TA',
          productId: 'PROD-this is busienss and batch',
          userLatitude: gpsLocation.latitude,
          userLongitude: gpsLocation.longitude
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Activation failed');
      }

      setResult({
        success: true,
        message: data.message,
        lotteryNumber: data.data.lotteryNumber,
        lotteryName: data.data.lotteryName
      });

      setTimeout(() => {
        setTagId('');
        setResult(null);
      }, 5000);

    } catch (error) {
      const err = error as Error;
      setResult({
        success: false,
        message: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => onNavigate ? onNavigate('dashboard') : window.location.href = '/store/dashboard'}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Tag Activation</h1>
            </div>
            <div className="text-sm text-gray-500">{store?.storeName}</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* GPS Status */}
        <div className={`mb-6 p-4 rounded-lg ${gpsLocation ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${gpsLocation ? 'text-green-800' : 'text-yellow-800'}`}>
                {gpsChecking ? '🔄 Getting GPS...' : gpsLocation ? '✅ GPS Ready' : '⚠️ GPS Required'}
              </p>
              {gpsLocation && (
                <p className="text-xs text-gray-600 mt-1">
                  {gpsLocation.latitude.toFixed(4)}, {gpsLocation.longitude.toFixed(4)}
                </p>
              )}
            </div>
            {!gpsLocation && !gpsChecking && (
              <button
                onClick={getGPSLocation}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm"
              >
                Enable GPS
              </button>
            )}
          </div>
        </div>

        {/* Activation Form */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">🎟️ Enter Tag for Lottery</h2>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Tag ID</label>
            <input
              type="text"
              value={tagId}
              onChange={(e) => setTagId(e.target.value)}
              placeholder="Enter or scan tag ID"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>

          {result && (
            <div className={`mb-6 p-4 rounded-lg ${
              result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <p className={`font-medium ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                {result.message}
              </p>
              {result.success && (
                <div className="mt-2 text-sm text-gray-600">
                  <p>🎟️ Lottery: {result.lotteryName}</p>
                  <p>🔢 Number: {result.lotteryNumber}</p>
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleActivate}
            disabled={loading || !gpsLocation || !tagId.trim()}
            className={`w-full py-4 rounded-lg font-semibold text-white text-lg ${
              loading || !gpsLocation || !tagId.trim()
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? '⏳ Activating...' : '🎫 Activate for Lottery'}
          </button>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800 font-medium">ℹ️ How it works:</p>
            <ul className="text-xs text-blue-700 mt-2 space-y-1 ml-4">
              <li>• GPS validates you're at the store</li>
              <li>• Tag enters active lottery campaign</li>
              <li>• Lottery number is generated</li>
              <li>• Draw happens on Dec 31, 2024</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export all components
export { StoreLogin, StoreDashboard, TagActivation };
export default StoreLogin;